import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Upload, FolderLock, Trash2, Download, FileText } from "lucide-react";
import { api, API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const fmtSize = (b) => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${(b / 1e3).toFixed(0)} KB`);

export default function Documents() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const { data: items = [] } = useQuery({
    queryKey: ["documents"],
    queryFn: async () => (await api.get("/documents")).data,
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/documents/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["documents"] }),
  });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "general");
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      qc.invalidateQueries({ queryKey: ["documents"] });
      toast.success("OK");
    } catch (err) {
      toast.error("Upload error");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = (id) => {
    window.open(`${API}/documents/${id}/download`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-4xl">{t("nav_documents")}</h1>
          <p className="text-muted-foreground text-sm mt-2">{t("documents_sub")}</p>
        </div>
        <input ref={fileRef} type="file" hidden onChange={onFile} data-testid="doc-file-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-doc-btn" className="rounded-sm gap-2">
          <Upload className="h-4 w-4" /> {uploading ? "..." : <span className="hidden sm:inline">{t("upload_document")}</span>}
        </Button>
      </div>

      {items.length === 0 ? (
        <Card className="p-16 rounded-sm border-dashed border-border text-center">
          <FolderLock className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">{t("empty_generic")}</p>
        </Card>
      ) : (
        <Card className="rounded-sm border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("filename")}</TableHead>
                <TableHead>{t("category")}</TableHead>
                <TableHead className="text-right">{t("size")}</TableHead>
                <TableHead>{t("uploaded")}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((d) => (
                <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
                  <TableCell className="font-medium flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" /> {d.original_filename}
                  </TableCell>
                  <TableCell><Badge variant="secondary" className="rounded-sm">{d.category}</Badge></TableCell>
                  <TableCell className="text-right tabular-nums">{fmtSize(d.size)}</TableCell>
                  <TableCell>{fmtDate(d.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" data-testid={`download-doc-${d.id}`} onClick={() => download(d.id)}><Download className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" data-testid={`del-doc-${d.id}`} onClick={() => remove.mutate(d.id)}
                      className="text-muted-foreground hover:text-[hsl(var(--destructive))]"><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
